import React from "react";
import { useGenesis } from "@/hooks/useGenesis";
import { GenesisToken } from "@/components/genesis/GenesisToken";
import { StatusChip, Countdown, ProgressBar, VerifiedTag, MintCopy, ExploreButton, BuyButton, RiskNote, fmtSol, fmtNum } from "@/components/genesis/GenesisBits";

function Term({ label, value, testid }) {
  return <div className="flex items-center justify-between gap-3 py-1.5 text-sm"><span className="text-am-text3">{label}</span><span className="text-right font-semibold text-white" data-testid={testid}>{value}</span></div>;
}

// Top-of-Market feature. Factual "ended" card after the sale; everything beneath it on Market is untouched.
export function GenesisMarketFeature() {
  const { data, status, countdown } = useGenesis();
  if (!data) return null;
  const t = data.terms;
  const ended = status === "ended";
  const buy = data.purchase_link_enabled && !ended;
  return (
    <section data-testid="genesis-market-feature" className="relative mt-5 overflow-hidden rounded-3xl border border-am-border bg-am-surface p-4 animate-[fadeIn_.4s_ease]">
      <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full aura-radial opacity-25 blur-3xl" />
      <div className="relative flex items-center gap-3">
        <GenesisToken size={56} parallax={false} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2"><h2 className="font-display text-base font-extrabold tracking-wide text-white md:text-lg">AURAMAX GENESIS</h2><StatusChip status={status} /></div>
          <p className="text-xs text-am-text2">{ended ? (data.progress ? `Genesis — ended · ${fmtSol(data.progress.sold_sol)} raised` : "Genesis — ended · final figures unavailable") : `$AURAMAX presale on ${data.provider.name} · paid in ${data.payment}`}</p>
          <div className="mt-1"><Countdown countdown={countdown} status={status} compact /></div>
        </div>
      </div>
      {buy && (
        <div className="relative mt-4">
          <BuyButton data={data} status={status} size="md" testid="genesis-market-official-link" surface="market" className="w-full" />
          <RiskNote className="mt-2 text-center" />
        </div>
      )}
      {!ended && (
        <div className="relative mt-3 space-y-3">
          <ProgressBar progress={data.progress} />
          <div className="divide-y divide-am-border rounded-2xl border border-am-border bg-am-bg/50 px-3">
            <Term label="Soft / hard cap" value={`${fmtSol(t.soft_cap_sol)} / ${fmtSol(t.hard_cap_sol)}`} testid="genesis-market-caps" />
            <Term label="Per wallet" value={`${fmtNum(t.min_sol)} – ${fmtNum(t.max_sol)} SOL`} testid="genesis-market-limits" />
            <Term label="Sale / listing rate" value={`${fmtNum(t.sale_rate)} / ${fmtNum(t.listing_rate)} per SOL`} testid="genesis-market-rates" />
          </div>
          <div className="flex flex-wrap items-center gap-2"><span className="text-xs text-am-text3">Mint</span><MintCopy mint={data.mint} /></div>
          <VerifiedTag data={data} />
        </div>
      )}
      <div className="relative mt-4 flex flex-wrap gap-2"><ExploreButton testid="genesis-market-explore" secondary={buy} /></div>
    </section>
  );
}
